import React from 'react'
import { useHasRole } from '../../utils/hooks/use-user-role'

/**
 * Component that conditionally renders children based on user role
 * 
 * @param {Object} props
 * @param {string|string[]} props.allowedRoles - Single role or array of roles that can see this content
 * @param {React.ReactNode} props.children - Content to render if user has permission
 * @param {React.ReactNode} [props.fallback] - Optional content to render if user doesn't have permission
 * @returns {React.ReactNode|null} Rendered content or null
 * 
 * @example
 * // Show button only to admin
 * <RoleBasedAccess allowedRoles="admin">
 *   <button>Admin Only Button</button>
 * </RoleBasedAccess>
 * 
 * @example
 * // Show content to admin or agent
 * <RoleBasedAccess allowedRoles={["admin", "agent"]}>
 *   <div>Admin or Agent Content</div>
 * </RoleBasedAccess>
 * 
 * @example
 * // Show fallback content if user doesn't have permission
 * <RoleBasedAccess allowedRoles="admin" fallback={<div>Access Denied</div>}>
 *   <button>Admin Only Button</button>
 * </RoleBasedAccess>
 */
const RoleBasedAccess = ({ allowedRoles, children, fallback = null }) => {
  const hasAccess = useHasRole(allowedRoles)
  
  if (hasAccess) {
    return <>{children}</>
  }
  
  return fallback ? <>{fallback}</> : null
}

export default RoleBasedAccess

